import { useState } from "react";
import { Calculator, TrendingUp, Moon, Building2, Sparkles } from "lucide-react";

const fmt = (n) => `NT$ ${Math.round(n).toLocaleString()}`;

// 順序對應首頁方案卡：輕量 → 成長 → 旗艦
const TIERS = [
  { key: "light", name: "輕量方案", fee: 38000, rate: 0.25, accent: "#0284C7" },
  { key: "growth", name: "人客多成長方案", fee: 56000, rate: 0.2, accent: "#0D9488" },
  { key: "flagship", name: "旗艦方案", fee: 78000, rate: 0.2, accent: "#FF6B00" },
];
const EXTRA_UNIT_FEE = 5000; // 每多一棟月費 +5,000

// 依推薦月營收級距推薦：<40萬 輕量、40–90萬 成長、≥90萬 旗艦
const bandKey = (R) => (R < 400000 ? "light" : R < 900000 ? "growth" : "flagship");

export const PlanRecommender = () => {
  const [rev, setRev] = useState("");
  const [units, setUnits] = useState(1);
  const R = parseInt(rev, 10);
  const valid = Number.isFinite(R) && R > 0;
  const extra = Math.max(0, units - 1);

  const cost = (t) => t.fee + extra * EXTRA_UNIT_FEE + t.rate * (valid ? R : 0);
  const net = (t) => (valid ? R : 0) - cost(t);

  const recKey = valid ? bandKey(R) : null;
  const rec = TIERS.find((t) => t.key === recKey);

  // 成長 vs 輕量、旗艦 每月實拿差
  const growth = TIERS.find((t) => t.key === "growth");
  const light = TIERS.find((t) => t.key === "light");
  const flagship = TIERS.find((t) => t.key === "flagship");
  const growthVsLight = valid ? net(growth) - net(light) : 0;
  const growthVsFlagship = valid ? net(growth) - net(flagship) : 0;

  return (
    <div data-testid="plan-recommender" className="max-w-3xl mx-auto mb-10 md:mb-12">
      <div className="bg-gradient-to-br from-[#0A2540] to-[#163859] rounded-3xl p-6 md:p-8 shadow-xl">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 md:w-10 md:h-10 bg-[#FF6B00] rounded-xl flex items-center justify-center flex-shrink-0">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg md:text-xl font-black text-white">現場試算：您每月實拿多少？</h3>
        </div>
        <p className="text-sm text-white/70 mb-5">
          輸入預估月營收與物件棟數，立即比較各方案<span className="text-[#F5C24A] font-bold">扣掉費用後您實際拿走</span>的金額。
        </p>

        <div className="grid md:grid-cols-3 gap-3 mb-5">
          <label className="md:col-span-2 block">
            <span className="text-xs font-medium text-white/60 mb-1.5 block">預估月營收（NT$）</span>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-sm font-medium">NT$</span>
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={rev}
                onChange={(e) => setRev(e.target.value)}
                placeholder="例：500000"
                data-testid="plan-recommender-input"
                className="w-full text-base md:text-lg font-bold bg-white/10 border border-white/20 rounded-xl pl-14 pr-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/60 focus:border-[#FF6B00]"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-white/60 mb-1.5 block flex items-center gap-1"><Building2 className="w-3 h-3" />物件棟數</span>
            <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-2 py-1.5">
              <button
                type="button"
                onClick={() => setUnits((u) => Math.max(1, u - 1))}
                data-testid="units-minus"
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white font-black transition-colors"
              >−</button>
              <div className="flex-1 text-center">
                <span className="text-lg md:text-xl font-black text-white">{units}</span>
                <span className="text-xs text-white/50 ml-1">棟</span>
              </div>
              <button
                type="button"
                onClick={() => setUnits((u) => Math.min(20, u + 1))}
                data-testid="units-plus"
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white font-black transition-colors"
              >＋</button>
            </div>
          </label>
        </div>

        {!valid ? (
          <div className="text-xs text-white/50 leading-relaxed">
            輸入月營收後，我們會依「月費＋抽成＋多棟加價」試算每個方案讓您<span className="text-white/80 font-semibold">實際拿走</span>的金額，並依營收規模推薦最適合的方案。
          </div>
        ) : (
          <div data-testid="plan-recommender-result">
            <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4">
              {TIERS.map((t) => {
                const isRec = t.key === recKey;
                return (
                  <div
                    key={t.key}
                    className={`rounded-2xl p-3 md:p-4 border transition-all ${isRec ? "bg-white/15" : "bg-white/5 border-white/10"}`}
                    style={isRec ? { borderColor: t.accent, boxShadow: `0 0 0 1px ${t.accent}` } : {}}
                  >
                    <div className="text-[11px] md:text-xs text-white/60 mb-1">{t.name}</div>
                    <div className="text-[10px] text-white/40">您每月實拿</div>
                    <div className="text-sm md:text-xl font-black text-white leading-tight">{fmt(net(t))}</div>
                    <div className="text-[10px] md:text-[11px] text-white/40 mt-1">付給我們 {fmt(cost(t))}</div>
                    {isRec && (
                      <div className="mt-1.5 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: t.accent, color: "#fff" }}>
                        推薦
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 成長方案關鍵洞察對比 */}
            <div data-testid="growth-insight" className="bg-gradient-to-br from-[#0D9488]/20 to-[#F5C24A]/10 border border-[#0D9488]/40 rounded-2xl px-4 py-3 mb-3">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-[#F5C24A] flex-shrink-0 mt-0.5" />
                <div className="text-xs md:text-sm text-white leading-relaxed">
                  <div className="mb-1"><span className="font-black text-[#F5C24A]">人客多成長方案關鍵對比</span>（以您輸入的月營收{units > 1 ? `、${units} 棟` : ""}試算）：</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    <div className="bg-black/25 rounded-lg px-3 py-2">
                      <div className="text-[10px] text-white/60">人客多成長 vs 輕量</div>
                      <div className={`text-sm md:text-base font-black ${growthVsLight >= 0 ? "text-[#F5C24A]" : "text-white/70"}`}>
                        {growthVsLight >= 0 ? "多賺 " : "少賺 "}{fmt(Math.abs(growthVsLight))}<span className="text-[10px] font-normal text-white/60">/月</span>
                      </div>
                    </div>
                    <div className="bg-black/25 rounded-lg px-3 py-2">
                      <div className="text-[10px] text-white/60">人客多成長 vs 旗艦</div>
                      <div className={`text-sm md:text-base font-black ${growthVsFlagship >= 0 ? "text-[#F5C24A]" : "text-white/70"}`}>
                        {growthVsFlagship >= 0 ? "多賺 " : "少花 "}{fmt(Math.abs(growthVsFlagship))}<span className="text-[10px] font-normal text-white/60">/月</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-2xl px-4 py-3">
              <div className="flex items-start gap-2">
                <TrendingUp className="w-4 h-4 text-[#F5C24A] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-white leading-relaxed">
                  以月營收 <span className="font-bold">{fmt(R)}</span>{units > 1 ? <>、<span className="font-bold">{units} 棟</span></> : null} 估算，最適合您的是
                  <span className="font-black text-[#F5C24A] mx-1">{rec.name}</span>，
                  您每月實拿約 <span className="font-black text-[#F5C24A]">{fmt(net(rec))}</span>
                </div>
              </div>

              {recKey === "flagship" ? (
                <div className="mt-2.5 flex items-start gap-2 bg-[#FF6B00]/15 border border-[#FF6B00]/40 rounded-xl px-3 py-2">
                  <Moon className="w-4 h-4 text-[#FF6B00] flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-white leading-relaxed">
                    <span className="font-black text-[#FF6B00]">最推薦旗艦</span>的關鍵：整套服務加上多棟庫存統一調度與跨棟導流、競品房價監測 — 棟數越多每棟越划算，適合多棟、高庫存、高營收業主。
                  </div>
                </div>
              ) : recKey === "growth" ? (
                <div className="mt-2.5 flex items-start gap-2 bg-[#0D9488]/15 border border-[#0D9488]/40 rounded-xl px-3 py-2">
                  <TrendingUp className="w-4 h-4 text-[#F5C24A] flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-white leading-relaxed">
                    <span className="font-black text-[#F5C24A]">人客多成長方案是您的甜蜜點</span>：廣告費全包，一次補齊 KOL、影片、PMS、客服到 23:00、進單分析 — 同樣 56,000 自己請人只剩 5,000 廣告預算、找代操只有投廣告。
                  </div>
                </div>
              ) : (
                <div className="mt-1 text-white/50 text-xs leading-relaxed">
                  ＊營收規模適中，輕量方案月費最低、您每月實拿最多；月營收預估將超過 40 萬時建議直接選人客多成長方案。
                </div>
              )}

              <div className="mt-2 text-white/40 text-[11px] leading-relaxed">
                ＊此為月費（每多一棟 +{fmt(EXTRA_UNIT_FEE)}）＋抽成後之預估實拿，實際服務內容與費用以合約為準。
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
